import React, {useEffect} from "react";
import {z} from "zod";
import LoadingButton from '@mui/lab/LoadingButton';

import InputContainer from "@/components/form/InputContainer.jsx";
import Input from '@/components/form/MUIInput.jsx';
import Form from "@/components/form/Form.jsx";
import {useForm} from "@/hooks/useForm.jsx";
import CharacterCounter from "@/components/form/CharacterCounter.jsx";
import {useFormProgress} from "@/hooks/useFormProgress.jsx";
import ContainerBox from "@/components/ContainerBox.jsx";
import {
    ContainerBoxHeader,
    ContainerBoxHeaderRight,
    ContainerBoxSkipLink,
    ContainerBoxTitle
} from "@/components/typography/index.jsx";
import { SIGN_UP_STUDENT_PROFILE_ENDPOINT} from "@/constants/endpoints.jsx";
import ProfilePhotoUpload from "@/components/form/ProfilePhotoUpload.jsx";
import PhoneMask from "@/components/form/masks/PhoneMask.jsx";
import {useLocalStorage} from "@/hooks/useLocalStorage.jsx";
import {DASHBOARD} from "@/constants/routes/index.jsx";

import DefaultEmptyIcon from '@svg/profile-thumbnail-108.svg';

const schema = z.object({
    logo: z.any().optional(),
    phone: z.string().optional(),
    bio: z.string().optional(),
}).refine((data) => {
    return data.logo || data.phone || data.bio;
}, {
    message: "At least one of 'logo', 'phone', or 'bio' must be filled",
    path: ['logo', 'phone', 'bio'], // Error will show on any of these fields
});


export default function StudentProfile({navigateOnSubmitTo = DASHBOARD, onSubmitCallback}) {
    const {setCurrentProgress} = useFormProgress();
    const {storeData, resetStoredData, getStoredData} = useLocalStorage();

    const {
        register,
        onSubmit,
        getValues,
        getFieldState,
        setValue,
        state,
        isValid,
        isLoading,
    } = useForm({
        endpoint: SIGN_UP_STUDENT_PROFILE_ENDPOINT,
        schema,
        defaultValues: {
            phone: '',
            bio: '',
        }
    });

    useEffect(() => {
        setCurrentProgress(3);
    }, []);

    useEffect(() => {
        const storedData = getStoredData();
        setValue('logo', storedData?.logo);
    }, [getStoredData]);

    if(!onSubmitCallback){
        onSubmitCallback = (endpointResponse, data) => {
            resetStoredData();

            if(endpointResponse?.data?.redirect) {
                window.location = endpointResponse.data.redirect;
            } else {
                window.location = navigateOnSubmitTo;
            }
        }
    }

    return (
        <>
            <ContainerBox>
                <ContainerBoxHeader>
                    <ContainerBoxTitle>Complete Your Profile</ContainerBoxTitle>
                    <ContainerBoxHeaderRight>
                        <ContainerBoxSkipLink onClick={() => {
                            onSubmitCallback();
                        }}>Skip</ContainerBoxSkipLink>
                    </ContainerBoxHeaderRight>
                </ContainerBoxHeader>

                <Form onSubmit={onSubmit(onSubmitCallback)}>
                    <InputContainer>
                        <ProfilePhotoUpload
                            image={getValues('logo')}
                            error={getFieldState('logo')?.error?.message ?? null}
                            uploadText="Photo"
                            borderRadius={54}
                            emptyIcon={<DefaultEmptyIcon/>}
                            imageEditor={true}
                            onChange={(file) => {
                                if(file) {
                                    setValue('logo', file?.origin);
                                    storeData('logo', file?.origin);
                                }
                            }}
                        />
                    </InputContainer>

                    <InputContainer>
                        <Input
                            label={"Phone Number"}
                            placeholder={"Phone Number"}
                            size={'md'}
                            inputComponent={PhoneMask}
                            {...register("phone")}
                            errorMessage={state?.errors?.phone?.message ?? ''}
                        />
                    </InputContainer>

                    <InputContainer>
                        <Input
                            label={'Job Seeker Bio'}
                            placeholder={!state?.errors?.bio?.message?.length ? 'Describe yourself and the job you’re looking for...' : ''}
                            fullWidth
                            multiline
                            {...register("bio")}
                            errorMessage={state?.errors?.bio?.message ?? ''}
                        />
                        <CharacterCounter value={getValues('bio')} max={1000}/>
                    </InputContainer>

                    <LoadingButton
                        loading={isLoading}
                        disabled={!isValid}
                        variant="contained"
                        type="submit"
                        size="medium"
                    >
                        Continue →
                    </LoadingButton>
                </Form>
            </ContainerBox>
        </>
    );
};
